import { HoverCard, Group, Text } from "@mantine/core"
import { Missing, PercentageCell, TextCell } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import type { MetalloplasticPercentageDto } from "~/gen/api/types/MetalloplasticPercentageDto"
import type { RecycledMaterialPercentageDto } from "~/gen/api/types/RecycledMaterialPercentageDto"

const NOT_CALCULATED = "not-calculated" as const
const ERROR = "error" as const

export type MaterialPercentageProps =
  | {
      readonly percentage: RecycledMaterialPercentageDto | null
      readonly type: "recycled"
    }
  | {
      readonly percentage: MetalloplasticPercentageDto | null
      readonly type: "metalloplastic"
    }

/**
 * AGEC regulations table: recycled / metalloplastic % with not-calculated, technical error, and >100% UX.
 */
export const MaterialPercentage = ({
  percentage,
  type,
}: MaterialPercentageProps) => {
  const { t } = useTranslation("products")
  const errorTooltip = t(
    "lists.cells.agec.materialPercentage.metadataComputationErrorTooltip"
  )
  const errorLabel = t(
    "lists.cells.agec.materialPercentage.computationErrorLabel"
  )

  if (!percentage) {
    return <Missing />
  }

  if (percentage.cause === NOT_CALCULATED) {
    if (type === "metalloplastic") {
      return (
        <HoverCard>
          <HoverCard.Target>
            <Group wrap="nowrap" align="center">
              -
            </Group>
          </HoverCard.Target>
          <HoverCard.Dropdown maw="32rem">
            <Text maw="100%" size="xs" w="fit-content">
              {t(
                "lists.cells.agec.materialPercentage.metalloplasticNotCalculatedTooltip"
              )}
            </Text>
          </HoverCard.Dropdown>
        </HoverCard>
      )
    } else {
      return <TextCell value="-" />
    }
  }

  if (percentage.cause === ERROR) {
    return <TextCell value={errorLabel} tooltip={errorTooltip} />
  }

  return (
    <PercentageCell
      isCalculated
      percentage={percentage.percentage}
      showOutOfRangeWarning
    />
  )
}
